// pcard-quick-add.js — the Quick Add modal and colour swatches on a .pcard
//
// This used to live in two places: the modal's UI state in an inline <script> at
// the bottom of sections/product-collection-general.liquid, and the part that
// turns a colour/size choice into a real cart add in custom-collection.js. The
// homepage rails now render the same card (snippets/pcard.liquid), so both halves
// moved here and are loaded on every template that shows one. Nothing about the
// behaviour changed in the move.
//
// The functions are global on purpose: the card markup wires them up through
// onclick attributes, which is what the snippet emits.
//
// Loaded from layout/theme.liquid for the collection, search and index templates.

/* ── QUICK ADD MODAL ── */

function qaOpen(btn) {
  document.querySelectorAll('.pcard.qa-open').forEach(card => qaReset(card));
  const card = btn.closest('.pcard');

  // Nothing to pick — no sizes, no colours in the modal — so opening an empty
  // panel just to press a second button is noise. Add it straight away.
  if (typeof qaHasChoices === 'function' && !qaHasChoices(card)) {
    qaAddDirect(card, btn);
    return;
  }

  card.classList.add('qa-open');
  btn.textContent = 'Add to Cart';
  btn.disabled = true;
  btn.onclick = (e) => { e.stopPropagation(); qaDoAdd(btn); };
  // Pre-select QA swatch to match active pcard-dot
  const activeDot = card.querySelector('.pcard-dot.active');
  if (activeDot) {
    const colour = activeDot.title;
    const modal = card.querySelector('.pcard-qa-modal');
    if (modal) {
      modal.querySelectorAll('.pcard-qa-swatch').forEach(s => {
        s.classList.toggle('active', s.title === colour);
      });
      const val = modal.querySelector('[data-colour-val]');
      if (val) val.textContent = colour;
    }
  }
  // Mark sizes for the pre-selected colour, and enable the button straight away
  // for products that have no size to pick (a colour is all they need).
  if (typeof qaSyncSizes === 'function') qaSyncSizes(card);
}

function qaClose(closeBtn) {
  qaReset(closeBtn.closest('.pcard'));
}

function qaReset(card) {
  if (!card) return;
  card.classList.remove('qa-open');
  const trigger = card.querySelector('.pcard-qa-trigger');
  if (trigger) {
    trigger.textContent = 'Quick Add';
    trigger.classList.remove('ready');
    trigger.disabled = false;
    trigger.onclick = (e) => { e.stopPropagation(); qaOpen(trigger); };
  }
  card.querySelectorAll('.pcard-qa-sz').forEach(b => b.classList.remove('active'));
}

function qaPickColour(swatch, e) {
  e.stopPropagation();
  const modal  = swatch.closest('.pcard-qa-modal');
  const card   = swatch.closest('.pcard');
  const colour = swatch.title;

  // Update QA swatch active state
  modal.querySelectorAll('.pcard-qa-swatch').forEach(s => s.classList.remove('active'));
  swatch.classList.add('active');
  const val = modal.querySelector('[data-colour-val]');
  if (val) val.textContent = colour;

  // Sync active state on pcard-dot
  card.querySelectorAll('.pcard-dot').forEach(d => {
    d.classList.toggle('active', d.title === colour);
  });

  // Update card main image if we have a URL
  if (swatch.dataset.img) {
    const img = card.querySelector('.pcard-real-img');
    if (img) img.src = swatch.dataset.img;
  }

  // Availability is per colour, so the size buttons have to be re-marked.
  if (typeof qaSyncSizes === 'function') qaSyncSizes(card);
}

function dotPickColour(dot) {
  const card   = dot.closest('.pcard');
  const colour = dot.title;

  // Update pcard-dot active state
  card.querySelectorAll('.pcard-dot').forEach(d => d.classList.remove('active'));
  dot.classList.add('active');

  // Update card image if we have a URL
  if (dot.dataset.img) {
    const img = card.querySelector('.pcard-real-img');
    if (img) img.src = dot.dataset.img;
  }

  // Sync QA swatch if modal is open
  if (card.classList.contains('qa-open')) {
    const modal = card.querySelector('.pcard-qa-modal');
    if (modal) {
      modal.querySelectorAll('.pcard-qa-swatch').forEach(s => {
        s.classList.toggle('active', s.title === colour);
      });
      const val = modal.querySelector('[data-colour-val]');
      if (val) val.textContent = colour;
    }
  }
}

function qaPickSize(btn, e) {
  e.stopPropagation();
  if (btn.classList.contains('oos')) return;
  const modal  = btn.closest('.pcard-qa-modal');
  const card   = btn.closest('.pcard');
  modal.querySelectorAll('.pcard-qa-sz').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');
  const trigger = card.querySelector('.pcard-qa-trigger');
  if (trigger) {
    trigger.classList.add('ready');
    trigger.disabled = false;
  }
}

function qaDoAdd(trigger) {
  // qaAddToCart (below) resolves the picked colour and size back to a variant id
  // and hands off to $360.addToCart, so the cart drawer and header count behave
  // exactly like everywhere else on the site.
  if (typeof qaAddToCart === 'function') { qaAddToCart(trigger); return; }
  console.warn('[quick add] add-to-cart half did not load');
}

// Click overlay background to close
document.addEventListener('click', e => {
  const overlay = e.target.closest('.pcard-qa-overlay');
  if (overlay && e.target === overlay) qaReset(overlay.closest('.pcard'));
});

// Esc closes all
document.addEventListener('keydown', e => {
  if (e.key === 'Escape')
    document.querySelectorAll('.pcard.qa-open').forEach(qaReset);
});

// Select a colour on a colour-grouped card.
//
// The dot is a real link to that colour's product, but a plain click should not
// leave the page: it swaps the thumbnail and retargets the card so the shopper
// lands on the colour they picked. Modified clicks keep the browser's own
// behaviour, so cmd/middle-click still opens the colour in a new tab.
function dotPickColourLink(dot, event) {
  if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
  if (event) event.preventDefault();

  const card = dot.closest('.pcard');
  if (!card) return;

  card.querySelectorAll('.pcard-dot').forEach(d => d.classList.remove('active'));
  dot.classList.add('active');

  if (dot.dataset.img) {
    const img = card.querySelector('.pcard-real-img');
    if (img) img.src = dot.dataset.img;
  }

  const href = dot.getAttribute('href');
  if (!href) return;

  card.querySelectorAll('a.pcard-img-wrap').forEach(a => a.setAttribute('href', href));

  // Remember what is selected so Quick Add cannot add the wrong colour (below).
  card.dataset.colourUrl  = href;
  card.dataset.colourSelf = dot.dataset.self === 'true' ? 'true' : 'false';

  // Quick Add must add the colour on screen. The card only ships its own
  // product's variants, so a sibling's have to be pulled in.
  if (dot.dataset.self === 'true') {
    delete card.__qaVariants;
    qaSyncSizes(card);
  } else {
    qaLoadSiblingVariants(card, href);
  }
}

// ── QUICK ADD: VARIANT RESOLUTION AND CART ───────────────────────────────────
// The markup renders the modal and tracks which colour/size is selected; this
// turns that selection into a real cart add. Each card carries a variant map
// (.pcard-variant-data) because the modal itself has no variant ids.

function qaCardVariants(card) {
  if (card.__qaVariants) return card.__qaVariants; // a sibling colour was fetched
  const el = card.querySelector('.pcard-variant-data');
  if (!el) return [];
  try {
    const list = JSON.parse(el.textContent);
    return Array.isArray(list) ? list : [];
  } catch (err) {
    console.warn('[quick add] variant data is not valid JSON:', err);
    return [];
  }
}

function qaSelection(card) {
  const modal = card.querySelector('.pcard-qa-modal');
  return {
    size: modal && modal.querySelector('.pcard-qa-sz.active')
      ? modal.querySelector('.pcard-qa-sz.active').textContent.trim()
      : null,
    // The modal swatch wins; the card dot is the fallback before the modal opens.
    colour: (modal && modal.querySelector('.pcard-qa-swatch.active')
      ? modal.querySelector('.pcard-qa-swatch.active').title
      : (card.querySelector('.pcard-dot.active') || {}).title) || null,
  };
}

function qaMatchVariant(variants, colour, size) {
  return variants.find(v => {
    const colourOk = !v.colour || !colour || v.colour.trim() === colour.trim();
    const sizeOk   = !v.size   || !size   || v.size.trim()   === size.trim();
    return colourOk && sizeOk;
  }) || null;
}

// Mark sizes that the selected colour does not actually have in stock, and keep
// the Add to Cart button in step with what is actually selectable.
function qaSyncSizes(card) {
  const variants = qaCardVariants(card);
  if (!variants.length) return;

  const colour   = qaSelection(card).colour;
  const sizeBtns = card.querySelectorAll('.pcard-qa-sz');
  const trigger  = card.querySelector('.pcard-qa-trigger');

  // Some products have no size option at all — bow headbands, for example, are
  // sold by colour only. Their modal has nothing to click, so the button would sit
  // disabled forever if it waited for a size. The colour alone identifies the
  // variant, so enable it as soon as the modal opens.
  if (!sizeBtns.length) {
    if (!trigger || !card.classList.contains('qa-open')) return;
    const variant  = qaMatchVariant(variants, colour, null);
    const sellable = !!variant && variant.available !== false;
    trigger.classList.toggle('ready', sellable);
    trigger.disabled = !sellable;
    return;
  }

  sizeBtns.forEach(btn => {
    const match = qaMatchVariant(variants, colour, btn.textContent.trim());
    const sellable = !!match && match.available !== false;
    btn.classList.toggle('oos', !sellable);
    if (!sellable) btn.classList.remove('active');
  });

  // The previously chosen size may have just been disabled.
  if (!card.querySelector('.pcard-qa-sz.active') && trigger && trigger.classList.contains('ready')) {
    trigger.classList.remove('ready');
    trigger.disabled = true;
  }
}

// True when the modal actually asks the shopper for something. Bow headbands, for
// instance, are sold in one size per colour-product, so their modal is empty.
function qaHasChoices(card) {
  return !!(card.querySelector('.pcard-qa-sz') || card.querySelector('.pcard-qa-swatch'));
}

// One click, no modal: for a product with nothing to choose, the current card
// state already identifies the variant.
function qaAddDirect(card, trigger) {
  if (!card || !trigger) return;
  qaCommitAdd(card, trigger);
}

function qaAddToCart(trigger) {
  if (!trigger || !trigger.classList.contains('ready')) return;

  const card = trigger.closest('.pcard');
  if (card) qaCommitAdd(card, trigger);
}

function qaCommitAdd(card, trigger) {
  const { colour, size } = qaSelection(card);
  const variant = qaMatchVariant(qaCardVariants(card), colour, size);

  if (!variant) {
    console.warn('[quick add] no variant for', { colour, size });
    alert('Sorry, that combination is unavailable.');
    return;
  }
  if (variant.available === false) {
    alert('Sorry, that size is sold out.');
    return;
  }

  // $360.addToCart owns the button's loading state, the cart drawer and the
  // header count — the same path every other add-to-cart on the site takes.
  if (typeof $360 === 'undefined' || typeof $360.addToCart !== 'function') {
    console.warn('[quick add] $360.addToCart unavailable');
    return;
  }

  // Where the add came from, for the cart line's analytics. The grid that owns
  // the card names itself; anything that does not keeps the collection value the
  // property has always carried.
  const scope = card.closest('[data-qa-location]');
  const location = scope ? scope.dataset.qaLocation : 'collection-quick-add';

  $360.addToCart(
    { qty: 1, variantId: variant.id, button: trigger, properties: { _location: location } },
    () => qaReset(card)
  );
}

// The .js endpoint for a product URL.
//
// Not `url + '.js'`: a product link is not always a bare path. On the search
// results page Shopify appends its own tracking parameters to every product URL
// it returns, so the dot's href is /products/<handle>?_pos=4&_sid=…&_ss=r and
// concatenating lands on "…&_ss=r.js", which is the product PAGE, not JSON. The
// fetch below then fails to parse, the card keeps the previous colour's variant
// map, and Quick Add offers sizes belonging to the wrong product.
//
// Appending to the pathname keeps any locale or market prefix (/en-sg/products/…)
// and drops the query, which the JSON endpoint has no use for.
function qaProductJsonUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    u.pathname = u.pathname.replace(/\.js$/, '') + '.js';
    u.search = '';
    u.hash = '';
    return u.pathname;
  } catch (err) {
    // Something that is not a URL at all — let the caller's catch handle it.
    return url;
  }
}

// A colour-grouped card only ships its own product's variants. When a sibling
// colour is picked, pull that product's variants in so Quick Add can add the
// colour on screen instead of sending the shopper away.
function qaLoadSiblingVariants(card, url) {
  if (!url) return;
  fetch(qaProductJsonUrl(url), { credentials: 'same-origin' })
    .then(res => res.ok ? res.json() : Promise.reject(new Error('HTTP ' + res.status)))
    .then(product => {
      const sizeIdx = (product.options || [])
        .findIndex(o => /size|age/i.test(o.name || o));
      card.__qaVariants = (product.variants || []).map(v => ({
        id: v.id,
        colour: '',
        // Empty, not the variant title, when the product has no size option —
        // matching how the Liquid map reports "no such option".
        size: sizeIdx >= 0 ? (v['option' + (sizeIdx + 1)] || '') : '',
        available: v.available,
      }));
      qaSyncSizes(card);
    })
    .catch(err => {
      // Leave the previous map in place rather than adding the wrong colour.
      console.warn('[quick add] could not load sibling variants:', err);
      delete card.__qaVariants;
      qaSyncSizes(card);
    });
}
